import MyModal from '../../../Common/my-modal/my-modal.component';
import ModalOptions from './modal-options';
import ModalBody from './modal-body';
import { Formik, FormikProps } from 'formik';
import { IEmailTemplateFormValues } from '../../../../common/validations/email-templates.validations';
import { ReactElement } from 'react';
import * as Yup from 'yup';

interface InviteTemplateModalProps<T extends IEmailTemplateFormValues> {
  title: string;
  show: boolean;
  onHide: () => void;
  initialValues: T;
  validationSchema: Yup.ObjectSchema<any>;
  formContentComponent: (formikProps: FormikProps<T>) => ReactElement;
  previewComponent: (formValues: T) => ReactElement;
  onSubmit?: (values: T) => void;
}

const InviteTemplateModal = <T extends IEmailTemplateFormValues>({ 
  title, 
  show, 
  onHide,
  initialValues,
  validationSchema,
  formContentComponent,
  previewComponent,
  onSubmit
}: InviteTemplateModalProps<T>) => {
  const handleSubmit = (values: T) => {
    onSubmit?.(values);
  };

  return (
    <MyModal show={show} onHide={onHide}>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        validateOnMount={true}
        enableReinitialize={true}
      >
        {(formikProps: FormikProps<T>) => (
          <FormContent 
            formikProps={formikProps} 
            title={title} 
            onHide={onHide}
            formContentComponent={formContentComponent}
            previewComponent={previewComponent}
          />
        )}
      </Formik>
    </MyModal>
  );
};

interface FormContentProps<T extends IEmailTemplateFormValues> {
  formikProps: FormikProps<T>;
  title: string;
  onHide: () => void;
  formContentComponent: (formikProps: FormikProps<T>) => ReactElement;
  previewComponent: (formValues: T) => ReactElement;
}

const FormContent = <T extends IEmailTemplateFormValues>({ 
  formikProps, 
  title, 
  onHide,
  formContentComponent,
  previewComponent
}: FormContentProps<T>) => {
  const { isValid, isSubmitting, handleSubmit, resetForm } = formikProps;

  const handleCancel = () => {
    resetForm();
    onHide();
  };

  const handleSave = () => {
    handleSubmit();
  };

  return (
    <MyModal.Body className='p-0 w-100 h-100 d-flex'>
      <ModalOptions 
        title={title} 
        formikProps={formikProps}
        onCancel={handleCancel}
        onSave={handleSave}
        isValid={isValid}
        isSubmitting={isSubmitting}
        formContentComponent={formContentComponent}
      />
      <ModalBody 
        formValues={formikProps.values}
        previewComponent={previewComponent}
      />
    </MyModal.Body>
  );
};

export default InviteTemplateModal;